import { createSelector } from "reselect"
import { chain, filter, get, pick, toLower } from "lodash"
import { computeLatestDate } from "helpers/dates"
import { computeBaseWeight, makeFindAndGetBaseMetadata, searchBase, computeSelectedBase, computeFullBenchmark, computeBaseHistory, VIEW_TYPES, filterAndOrderBaseInstruments } from "helpers/base"
import { getBenchmarksLookup, getPortfoliosWithMetadata } from "selectors/portfolios"
import { createQueryStringSelector, getRawSelectedBase, getSelectedBaseType } from "selectors/queryString"
import { getRecentInstruments } from "selectors/recentInstruments"
import { getIndices } from "selectors/indices"
import { getBaskets } from "resources/baskets"
import { getModelDefaultBenchmark } from "selectors/modelMetadata"
import { computeAssetTickerMic } from "../helpers"

const EMPTY_ARRAY = []

export const getIsActive = createQueryStringSelector("isActive", false)
export const getBaseSearchValue = state => get(state, "modules.analyzeCompositeSecurity.baseSearchValue")
export const getBaseDropdownVisible = state => get(state, "modules.analyzeCompositeSecurity.baseDropdownVisible")
const getPreviousModelBase = state => get(state, "modules.analyzeCompositeSecurity.previousModelSelectedBaseId")
const getRawETFs = state => get(state, "global.data.etfs.data", EMPTY_ARRAY)
const getAvailableBenchmarks = state => get(state, "global.data.benchmarksAndPortfolios.data.model.benchmarks")
const getResourceMeta = state => get(state, "global.data.resourceMeta.data")
const getAssetMeta = state => get(state, "global.data.assetMeta.data")
const getAssetDetails = (state) => get(state, "global.data.assetDetails.data")
const getAssetType = (_, props) => toLower(get(props, "params.assetType"))
const getModelEndDate = state => get(state, "global.data.dates.data.model.availability.currentDate")
const getAssetEndDate = state => get(state, "global.data.assetDetails.data.availableTo")
const getRawBaseHistory = state => get(state, "modules.analyzeCompositeSecurity.baseHistory")

const getAssetTickerMic = createSelector(
  [getAssetDetails],
  computeAssetTickerMic,
)

const getLatestDate = createSelector(
  [getAssetEndDate, getModelEndDate],
  computeLatestDate,
)

const getMeta = createSelector(
  [getResourceMeta, getAssetMeta, getAssetType],
  (resourceMeta, assetMeta, assetType) => assetType === "basket" ? resourceMeta : assetMeta,
)

const getAssetDefaultBenchmark = createSelector(
  [getMeta],
  (meta) => get(meta, "defaultBenchmark", ""),
)

const getLongShortType = createSelector(
  [getMeta],
  (meta) => get(meta, "type", ""),
)

export const getResourceId = createSelector([getResourceMeta], resourceMeta => get(resourceMeta, "id"))

const getComputedWeight = createSelector(
  [getLongShortType],
  computeBaseWeight,
)

const getValidAsBasePortfolios = createSelector(
  [getPortfoliosWithMetadata, getLatestDate],
  (portfolios, latestDate) => filter(portfolios, ({ availableFrom }) => availableFrom && latestDate >= availableFrom),
)

const filterCurrentEtf = (etf, assetTickerMic) => !assetTickerMic || !(assetTickerMic.ticker === etf.ticker && assetTickerMic.mic === etf.mic)

const getETFs = createSelector(
  [getRawETFs, getAssetTickerMic],
  (etfs, assetTickerMic) => chain(etfs)
    .filter(etf => filterCurrentEtf(etf, assetTickerMic))
    .map(etf => ({
      ...pick(etf, ["ticker", "mic", "description", "availableFrom", "internalId", "hasComposition"]),
      id: `${etf.ticker}.${etf.mic}`,
    }))
    .value(),
)

const getFindAndGetBaseMetadata = createSelector(
  [getComputedWeight, getBenchmarksLookup, getValidAsBasePortfolios, getIndices, getETFs, getBaskets],
  (weight, benchmarks, portfolios, indices, etfs, baskets) => makeFindAndGetBaseMetadata(weight, { benchmarks, portfolios, indices, etfs, baskets }),
)

export const getPreviousModelBaseUsed = createSelector(
  [getPreviousModelBase, getFindAndGetBaseMetadata],
  (previousModelBase, findAndGetBaseMetadata) => findAndGetBaseMetadata(previousModelBase),
)

const getRawBaseHistoryWithRecentInstruments = createSelector(
  [getRecentInstruments, getRawBaseHistory],
  filterAndOrderBaseInstruments,
)

export const getBaseHistory = createSelector(
  [getRawBaseHistoryWithRecentInstruments, getComputedWeight, getBenchmarksLookup, getValidAsBasePortfolios, getIndices, getETFs, getBaskets],
  (rawBaseHistory, weight, benchmarks, portfolios, indices, etfs, baskets) => computeBaseHistory(rawBaseHistory, weight, { benchmarks, portfolios, indices, etfs, baskets }),
)

export const getSelectedBase = createSelector(
  [getRawSelectedBase, getSelectedBaseType, getFindAndGetBaseMetadata, getPreviousModelBaseUsed],
  (selectedBaseId, selectedBaseType, findAndGetBaseMetadata, previousModelBaseUsed) => {
    const selectedBase = findAndGetBaseMetadata({ id: selectedBaseId, type: selectedBaseType })
    return computeSelectedBase([], selectedBase, null, previousModelBaseUsed)
  },
)

export const getBaseStartDate = createSelector(
  [getSelectedBase],
  base => get(base, "availableFrom"),
)

export const getDefaultBenchmark = createSelector(
  [getModelDefaultBenchmark, getAssetDefaultBenchmark, getSelectedBase],
  (modelBenchmark, assetBenchmark, selectedBase) => get(selectedBase, "id") || assetBenchmark || modelBenchmark,
)

export const getFullBenchmark = createSelector(
  [getDefaultBenchmark, getBenchmarksLookup, getLongShortType],
  computeFullBenchmark,
)

export const getBaseSearchResults = createSelector(
  [getBaseSearchValue, getComputedWeight, getAvailableBenchmarks, getValidAsBasePortfolios, getIndices, getETFs, getBaskets],
  (searchValue, weight, benchmarks, portfolios, indices, etfs, baskets) => searchBase(searchValue, weight, { benchmarks, portfolios, indices, etfs, baskets }),
)

export const getBasePositionSet = createSelector(
  [getFullBenchmark, getSelectedBase],
  (benchmark, selectedBase) => {
    if (selectedBase) {
      return {
        type: selectedBase.type,
        id: selectedBase.resourceId || selectedBase.id,
        weight: selectedBase.weight,
        ...(selectedBase.experimentId && { experimentId: selectedBase.experimentId }),
      }
    }

    return {
      type: "BENCHMARK",
      id: benchmark.id,
      weight: benchmark.weight,
    }
  },
)

export const getBaseSignature = createSelector(
  [getBasePositionSet],
  (base) => base.id,
)

export const getViewType = createSelector(
  [getIsActive],
  (isActive) => isActive ? VIEW_TYPES.ACTIVE : VIEW_TYPES.ABSOLUTE,
)
